import { FC, useMemo, useState } from 'react'
import IconError from 'assets/error-warning-fill.svg?react'
import IconPlus from 'assets/plus.svg?react'
import IconSearch from 'assets/search.svg?react'
import cn from 'classnames'
import { Search } from 'components/extend'
import { Button } from 'components/extend/Button'
import { ListRow } from 'components/extend/List'
import { PlaceholderCell } from 'components/extend/PlaceholderCell'
import { useOnMount } from 'hooks'
import { useDebounce } from 'hooks/useDebounce'
import { useFindAllRecommendedCustomerQuery } from 'modules/customer/api'
import { getCustomerInnKpp, getCustomerName } from 'modules/customer/helpers'
import { TSelectCustomer } from 'modules/order/types'

import {
  GetCustomerWithRecommendedReturn,
  RecommendedCustomer,
  RecommendedCustomerIds,
  SelectCustomerProps
} from './types'

import s from './styles.module.less'

export const SelectCustomer: FC<SelectCustomerProps> = (props) => {
  const {
    customers,
    setSelectedCustomer,
    isShowSelect,
    isCustomerListLoading,
    handleShowSelect,
    handleHideSelect,
    errorMessage,
    handleOpenCustomerAddModal,
    setCustomerId
  } = props
  const {
    data: recommendedCustomer,
    isFetching: isRecommendedCustomerFetching,
    refetch
  } = useFindAllRecommendedCustomerQuery()

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const debouncedSearchCustomer = useDebounce(debouncedSearchTerm, 500)
  const [filteredSearchTerm, setFilteredSearchTerm] = useState(debouncedSearchCustomer)

  const handleSearchCustomer = (search: string) => {
    setDebouncedSearchTerm(search)
    setFilteredSearchTerm(search)
  }

  const handleSelectValue = (el: RecommendedCustomer) => {
    // TODO удалить каст
    setSelectedCustomer(el as unknown as TSelectCustomer)
    setCustomerId(el.id)
  }

  const hasDivider = (el: RecommendedCustomer) => {
    const customersWithoutRecommended = (customers || []).filter(
      (item) => !recommendedCustomer?.find((el) => el.id === item.id)
    )

    if (!customersWithoutRecommended?.length) {
      return false
    }

    if (!recommendedCustomer) {
      return false
    }
    const lastRecommendedCustomer = recommendedCustomer?.at(-1)
    if (!lastRecommendedCustomer) {
      return false
    }

    return el.id === lastRecommendedCustomer?.id
  }

  const filteredCustomerList = useMemo(() => {
    const getCustomerWithRecommended = (): GetCustomerWithRecommendedReturn => {
      if (!recommendedCustomer || !customers) {
        return []
      }
      const recommendedCustomerIds: RecommendedCustomerIds = {}
      const mappedRecommendedCustomer: GetCustomerWithRecommendedReturn = recommendedCustomer.map((customer) => {
        recommendedCustomerIds[customer.id] = true

        return { ...customer, recommended: true }
      })

      return mappedRecommendedCustomer.concat(customers.filter((customer) => !recommendedCustomerIds[customer.id]))
    }
    const customerWithRecommended = getCustomerWithRecommended()

    if (!filteredSearchTerm) {
      return customerWithRecommended
    }

    return customerWithRecommended.filter((item) => item.name.toLowerCase().includes(filteredSearchTerm.toLowerCase()))
  }, [filteredSearchTerm, customers, recommendedCustomer])

  useOnMount(() => {
    async function init() {
      await refetch()
    }

    if (isShowSelect) {
      init()
    }
  })

  return (
    <>
      <div className={cn(s.selectBlock, { [s.selectBlock__error]: errorMessage })} style={{ zIndex: 2 }}>
        <div className={s.customerSelectBlockTitle}>
          Выберите заказчика
          {errorMessage && <IconError />}
        </div>
        {isShowSelect ? (
          <div className={s.customerSelectBlock}>
            <div className={s.selectContainer} onClick={handleHideSelect} />
            <Search
              isInDetailPage
              className={cn(s.select, { [s.select__white]: isShowSelect })}
              handleSearch={handleSearchCustomer}
              isShowSelect={isShowSelect}
              placeholder="Начните вводить"
              searchQuery={filteredSearchTerm || ''}
            />
            <div className={s.customersBlock}>
              <div className={s.customersList}>
                {/* eslint-disable-next-line */}
                {customers?.length ? (
                  // eslint-disable-next-line
                  !isCustomerListLoading || !isRecommendedCustomerFetching ? (
                    filteredCustomerList.length ? (
                      filteredCustomerList.map((el, index) =>
                        el?.recommended ? (
                          <div key={index}>
                            <ListRow
                              className={cn(s.optionCustomer, s.customers)}
                              onClick={() => handleSelectValue(el)}
                            >
                              <div className={cn(s.customerName)}>{getCustomerName(el)}</div>
                              <div className={cn(s.customerInnKpp)}>{getCustomerInnKpp(el)}</div>
                            </ListRow>
                            {hasDivider(el) && <div className={s.divider} />}
                          </div>
                        ) : (
                          <ListRow
                            key={index}
                            className={cn(s.optionCustomer, s.customers)}
                            onClick={() => handleSelectValue(el)}
                          >
                            <div className={cn(s.customerName)}>{getCustomerName(el)}</div>
                            <div className={cn(s.customerInnKpp)}>{getCustomerInnKpp(el)}</div>
                          </ListRow>
                        )
                      )
                    ) : (
                      <div className={s.notFound}>Совпадений не найдено</div>
                    )
                  ) : (
                    <PlaceholderCell height={52} />
                  )
                ) : (
                  <div className={s.notFound}>У вас пока не добавлено ни одного контрагента</div>
                )}
              </div>
              <div className={s.customersAddBlock}>
                <Button appearance="transparent" className={s.createBtn} onClick={handleOpenCustomerAddModal} size="nm">
                  <IconPlus size="xs" />
                  Добавить
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <Button className={cn(s.select, s.selectBtn)} onClick={handleShowSelect} size="lg">
            <IconSearch className={cn({ [s.iconSearch__blue]: !isShowSelect })} size="xs" />
            Выбрать
          </Button>
        )}
      </div>
    </>
  )
}
